/**
 * Seeded randomness.
 *
 * Every random choice in a piece comes from here, and a piece always carries
 * its seed. Two reasons, and the second is the one that matters.
 *
 * A generated track you cannot regenerate is an asset, not a format: you would
 * have to keep the WAV forever because the thing that made it is gone. With a
 * seed, the track *is* the twelve lines that describe it.
 *
 * And when someone hits randomise forty times and one of them is good, the seed
 * is the only thing that lets them keep it.
 */

/** mulberry32: small, fast, and good enough for choosing notes. */
export function makeRng(seed: number): Rng {
  let state = seed >>> 0;

  const next = (): number => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  return {
    seed,
    next,
    int: (maxExclusive: number) => Math.floor(next() * maxExclusive),
    pick: <T>(items: readonly T[]): T => items[Math.floor(next() * items.length)],
    chance: (probability: number) => next() < probability,
    range: (min: number, max: number) => min + next() * (max - min),
    /**
     * Pick from `first` with probability `bias`, otherwise from `second`.
     * Falls back to whichever list is non-empty rather than returning
     * undefined, because an empty pool is a config problem and should not also
     * become a crash three layers down.
     */
    weighted: <T>(first: readonly T[], second: readonly T[], bias: number): T => {
      if (!first.length) return second[Math.floor(next() * second.length)];
      if (!second.length) return first[Math.floor(next() * first.length)];
      return next() < bias
        ? first[Math.floor(next() * first.length)]
        : second[Math.floor(next() * second.length)];
    },
  };
}

export interface Rng {
  seed: number;
  next(): number;
  int(maxExclusive: number): number;
  pick<T>(items: readonly T[]): T;
  chance(probability: number): boolean;
  range(min: number, max: number): number;
  weighted<T>(first: readonly T[], second: readonly T[], bias: number): T;
}
